/**
 * Solitaire (Klondike) Store - Game state management
 */

import { create } from 'zustand';

// Card suits and their properties
const SUITS = {
  hearts: { symbol: '♥', color: 'red' },
  diamonds: { symbol: '♦', color: 'red' },
  clubs: { symbol: '♣', color: 'black' },
  spades: { symbol: '♠', color: 'black' }
};

const RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

// Create a fresh deck of 52 cards
const createDeck = () => {
  const deck = [];
  let id = 0;
  
  for (const suit of Object.keys(SUITS)) {
    for (let rankIndex = 0; rankIndex < RANKS.length; rankIndex++) {
      deck.push({
        id: id++,
        suit,
        rank: RANKS[rankIndex],
        rankValue: rankIndex + 1, // 1-13 (Ace=1, King=13)
        color: SUITS[suit].color,
        symbol: SUITS[suit].symbol,
        faceUp: false
      });
    }
  }
  
  return deck;
};

// Fisher-Yates shuffle
const shuffleDeck = (deck) => {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Deal cards to tableau (7 piles, 1-7 cards each)
const dealTableau = (deck) => {
  const tableau = [[], [], [], [], [], [], []];
  let cardIndex = 0;
  
  for (let pile = 0; pile < 7; pile++) {
    for (let card = 0; card <= pile; card++) {
      const isTopCard = card === pile;
      tableau[pile].push({
        ...deck[cardIndex],
        faceUp: isTopCard
      });
      cardIndex++;
    }
  }
  
  // Remaining cards go to stock
  const stock = deck.slice(cardIndex).map(card => ({ ...card, faceUp: false }));
  
  return { tableau, stock };
};

// Check if a card can be placed on a tableau pile
const canPlaceOnTableau = (card, targetPile) => {
  if (targetPile.length === 0) {
    // Empty pile - only Kings can be placed
    return card.rankValue === 13;
  }
  
  const topCard = targetPile[targetPile.length - 1];
  
  // Must be face up, opposite color, and one rank lower
  return topCard.faceUp && 
         card.color !== topCard.color && 
         card.rankValue === topCard.rankValue - 1;
};

// Check if a card can be placed on a foundation pile
const canPlaceOnFoundation = (card, foundation) => {
  if (foundation.length === 0) {
    // Empty foundation - only Aces can be placed
    return card.rankValue === 1;
  }
  
  const topCard = foundation[foundation.length - 1];
  
  // Must be same suit and one rank higher
  return card.suit === topCard.suit && 
         card.rankValue === topCard.rankValue + 1;
};

// Check if game is won (all cards in foundations)
const checkWin = (foundations) => {
  return foundations.every(f => f.length === 13);
};

// Initial state
const INITIAL_STATE = {
  // Game areas
  stock: [],           // Draw pile (face down)
  waste: [],           // Drawn cards (face up)
  foundations: [[], [], [], []], // 4 foundation piles (Aces to Kings by suit)
  tableau: [[], [], [], [], [], [], []], // 7 tableau piles
  
  // Game state
  gameStarted: false,
  gameWon: false,
  moves: 0,
  score: 0,
  time: 0,
  timerRunning: false,
  
  // Draw mode: 1 or 3 cards
  drawCount: 1,
  
  // Selection for click-to-move
  selectedCards: null,
  selectedSource: null,
  
  // Undo stack
  history: [],
  
  // Stats
  gamesPlayed: 0,
  gamesWon: 0
};

const useSolitaireStore = create((set, get) => ({
  ...INITIAL_STATE,
  
  // Start a new game
  startGame: (drawCount = 1) => {
    const deck = shuffleDeck(createDeck());
    const { tableau, stock } = dealTableau(deck);
    
    set({
      ...INITIAL_STATE,
      stock,
      tableau,
      drawCount,
      gameStarted: true,
      timerRunning: true,
      gamesPlayed: get().gamesPlayed + 1
    });
  },
  
  // Draw cards from stock to waste
  drawCards: () => {
    const state = get();
    if (!state.gameStarted || state.gameWon) return;
    
    const { stock, waste, drawCount, history } = state;
    
    // Save current state for undo
    const newHistory = [...history, {
      stock: [...stock],
      waste: [...waste],
      foundations: state.foundations.map(f => [...f]),
      tableau: state.tableau.map(t => [...t]),
      moves: state.moves,
      score: state.score
    }];
    
    if (stock.length === 0) {
      // Recycle waste back to stock
      if (waste.length === 0) return;
      
      const newStock = waste.reverse().map(card => ({ ...card, faceUp: false }));
      set({
        stock: newStock,
        waste: [],
        history: newHistory
      });
    } else {
      // Draw cards from stock
      const numToDraw = Math.min(drawCount, stock.length);
      const drawnCards = stock.slice(-numToDraw).map(card => ({ ...card, faceUp: true }));
      const newStock = stock.slice(0, -numToDraw);
      
      set({
        stock: newStock,
        waste: [...waste, ...drawnCards],
        moves: state.moves + 1,
        history: newHistory
      });
    }
  },
  
  // Select cards for moving
  selectCards: (cards, source) => {
    set({ selectedCards: cards, selectedSource: source });
  },
  
  // Clear selection
  clearSelection: () => {
    set({ selectedCards: null, selectedSource: null });
  },
  
  // Move cards from one location to another
  moveCards: (cards, source, destination) => {
    const state = get();
    if (!state.gameStarted || state.gameWon) return false;
    
    const { tableau, foundations, waste, history } = state;
    
    // Validate move based on destination
    let isValidMove = false;
    
    if (destination.type === 'tableau') {
      isValidMove = canPlaceOnTableau(cards[0], tableau[destination.index]);
    } else if (destination.type === 'foundation') {
      // Only single cards can go to foundation
      if (cards.length === 1) {
        isValidMove = canPlaceOnFoundation(cards[0], foundations[destination.index]);
      }
    }
    
    if (!isValidMove) {
      get().clearSelection();
      return false;
    }
    
    // Save current state for undo
    const newHistory = [...history, {
      stock: [...state.stock],
      waste: [...waste],
      foundations: foundations.map(f => [...f]),
      tableau: tableau.map(t => [...t]),
      moves: state.moves,
      score: state.score
    }];
    
    // Remove cards from source
    let newWaste = [...waste];
    let newTableau = tableau.map(t => [...t]);
    let newFoundations = foundations.map(f => [...f]);
    let scoreGain = 0;
    
    if (source.type === 'waste') {
      newWaste = waste.slice(0, -1);
      scoreGain = destination.type === 'foundation' ? 10 : 5;
    } else if (source.type === 'tableau') {
      const pile = newTableau[source.index];
      const cardIndex = pile.findIndex(c => c.id === cards[0].id);
      newTableau[source.index] = pile.slice(0, cardIndex);
      
      // Flip the new top card if it exists and is face down
      if (newTableau[source.index].length > 0) {
        const topCard = newTableau[source.index][newTableau[source.index].length - 1];
        if (!topCard.faceUp) {
          newTableau[source.index][newTableau[source.index].length - 1] = { ...topCard, faceUp: true };
          scoreGain += 5; // Bonus for revealing card
        }
      }
      
      scoreGain += destination.type === 'foundation' ? 10 : 0;
    } else if (source.type === 'foundation') {
      newFoundations[source.index] = foundations[source.index].slice(0, -1);
      scoreGain = -15; // Penalty for moving from foundation
    }
    
    // Add cards to destination
    if (destination.type === 'tableau') {
      newTableau[destination.index] = [...newTableau[destination.index], ...cards];
    } else if (destination.type === 'foundation') {
      newFoundations[destination.index] = [...newFoundations[destination.index], ...cards];
    }
    
    // Check for win
    const won = checkWin(newFoundations);
    
    set({
      waste: newWaste,
      tableau: newTableau,
      foundations: newFoundations,
      moves: state.moves + 1,
      score: Math.max(0, state.score + scoreGain),
      selectedCards: null,
      selectedSource: null,
      history: newHistory,
      gameWon: won,
      timerRunning: !won,
      gamesWon: won ? state.gamesWon + 1 : state.gamesWon
    });
    
    return true;
  },
  
  // Auto-move card to foundation if possible
  autoMoveToFoundation: (card, source) => {
    const { foundations } = get();
    
    for (let i = 0; i < foundations.length; i++) {
      if (canPlaceOnFoundation(card, foundations[i])) {
        return get().moveCards([card], source, { type: 'foundation', index: i });
      }
    }
    
    return false;
  },
  
  // Undo last move
  undo: () => {
    const { history } = get();
    if (history.length === 0) return;
    
    const lastState = history[history.length - 1];
    set({
      stock: lastState.stock,
      waste: lastState.waste,
      foundations: lastState.foundations,
      tableau: lastState.tableau,
      moves: lastState.moves,
      score: Math.max(0, lastState.score - 5), // Small penalty for undo
      history: history.slice(0, -1),
      selectedCards: null,
      selectedSource: null
    });
  },
  
  // Update timer
  tick: () => {
    const { timerRunning, time } = get();
    if (timerRunning) {
      set({ time: time + 1 });
    }
  },
  
  // Toggle draw count (1 or 3)
  toggleDrawCount: () => {
    const { drawCount } = get();
    set({ drawCount: drawCount === 1 ? 3 : 1 });
  },
  
  // Reset game
  resetGame: () => {
    set(INITIAL_STATE);
  }
}));

export default useSolitaireStore;
export { SUITS, RANKS };
